import React from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import ChatReply from './ChatReply';
import UserAvatar from '../user-avatar/UserAvatar';
import timeAgo from '../../Util/timeAgo';
import { chatAvatarSx, chatColors, chatRadii, chatSendSx, chatSizes } from './chatTheme';

// Slide and Question tags share one chip; only the fill changes.
const chipSx = (background) => (theme) => ({
    fontSize: 11,
    fontWeight: 500,
    lineHeight: 1.3,
    minWidth: 9,
    padding: '4px 8px',
    borderRadius: chatRadii.chip,
    textAlign: 'center',
    color: theme.palette.common.white,
    backgroundColor: background(chatColors(theme))
});

// The comment's action row and the replies toggle: same shape, different
// emphasis. (The "Like" button joins the actions row in the next PR.)
const actionSx = (theme) => ({
    fontSize: 12,
    fontWeight: 500,
    padding: '6px 8px',
    borderRadius: chatRadii.action,
    color: chatColors(theme).textMuted,
    '&:hover': { backgroundColor: chatColors(theme).hoverSurface }
});

const toggleSx = (theme) => ({
    ...actionSx(theme),
    mt: 0.75,
    color: chatColors(theme).primary,
    '&:hover': { backgroundColor: chatColors(theme).primaryTint }
});

/**
 * One flat comment row: avatar, then a meta line (name + tags + relative time)
 * above the body, and under it the thread - a Reply action, the replies behind
 * a toggle, and the inline reply field wherever it is open. Every row is laid
 * out identically - no own-vs-other tinting and no bubble. `now` comes from the
 * panel's shared interval so the time label stays current.
 *
 * The field sits under whatever was replied to: directly below this comment's
 * body when Reply was pressed here, and below a particular reply when it was
 * pressed there - `replyAnchorId` says which. That is placement only; a reply
 * still attaches to this comment however deep the user clicked, so threads stay
 * one level deep.
 *
 * Slide numbers arrive already resolved from the server, never looked up
 * client-side: a tag can outlive its slide, and the deleted slide is never sent
 * to a guest at all.
 *
 * The thread's state lives in the panel, not here: which comment is being
 * replied to is a property of the panel (only one field is open at a time), and
 * the draft has to survive this row re-rendering as messages reload.
 */
export default function ({
    name,
    message,
    time,
    now = Date.now(),
    isAdmin = false,
    slideTags = [],
    question = false,
    replies = [],
    readOnly = false,
    expanded = true,
    onToggleExpanded = null,
    replying = false,
    replyAnchorId = null,
    replyDraft = '',
    replyFocusKey = 0,
    replySending = false,
    onReply = null,
    onReplyDraftChange = null,
    onReplySubmit = null,
    onReplyCancel = null
}) {
    const showReplies = expanded && replies.length > 0;
    const repliesLabel = `${expanded ? 'Hide' : 'View'} ${replies.length} ${
        replies.length === 1 ? 'reply' : 'replies'
    }`;

    // A reply attaches to this comment however deep the user clicked: replying
    // to a reply only moves the field under it and addresses its author by name.
    const replyToReply = (reply) =>
        onReply(reply.id, `@${(reply.name || '').trim().split(' ')[0]} `);

    // Where the open field belongs. The anchor is dropped rather than obeyed
    // when its reply isn't on screen - the thread collapsed under it, or a
    // reload no longer carries it - so the field falls back to this comment
    // instead of disappearing with a draft still in it.
    const anchorIndex =
        replyAnchorId == null ? -1 : replies.findIndex((reply) => reply.id === replyAnchorId);
    const anchored = !readOnly && replying && showReplies && anchorIndex >= 0;
    // Whoever is being answered, which is the anchored reply's author even when
    // the anchor itself has been dropped: the draft still opens "@FirstName ".
    const replyTargetName = anchorIndex >= 0 ? replies[anchorIndex].name : name;

    // Both keys are swallowed even while a send is out - Enter so a held key
    // can't break the line, Escape so it can't appear to cancel a reply that is
    // already on its way.
    const handleReplyKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (!replySending) {
                onReplySubmit();
            }
        } else if (e.key === 'Escape') {
            e.preventDefault();
            onReplyCancel();
        }
    };

    // Built once and rendered in exactly one of two places. Never inserted into
    // the replies list as a keyed sibling: React would then *move* the live DOM
    // node between anchors, which blurs an input, instead of remounting it.
    const replyField = (
        <Box
            sx={(theme) => ({
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mt: 1,
                // Between two replies the list's own 2px gap would leave the
                // field all but touching the one below and clearly separated
                // from the one it answers. Matched to the top margin so it
                // reads as belonging to the reply above it.
                mb: anchored ? 1 : 0,
                border: `1px solid ${chatColors(theme).border}`,
                borderRadius: chatRadii.input,
                padding: '4px 4px 4px 12px'
            })}>
            <InputBase
                // Remounted on every Reply press, which is what focuses the
                // field and drops the caret after a "@FirstName " prefill when
                // it was already open - including when the press only moved it
                // to another anchor in the same thread.
                key={replyFocusKey}
                autoFocus
                fullWidth
                placeholder={`Reply to ${replyTargetName}`}
                value={replyDraft}
                // Read-only rather than disabled for the moment a send is out:
                // the field keeps focus and caret, so typing resumes where it
                // left off if the send fails - and nothing typed meanwhile is
                // left to be thrown away when the send lands and closes it.
                readOnly={replySending}
                onChange={(e) => onReplyDraftChange(e.target.value)}
                onKeyDown={handleReplyKeyDown}
                sx={{ flex: '1 1 auto', minWidth: 0, fontSize: 13, color: 'text.primary' }}
            />
            <IconButton
                aria-label={`Send reply to ${replyTargetName}`}
                onClick={onReplySubmit}
                disabled={!replyDraft.trim() || replySending}
                sx={chatSendSx(chatSizes.replySend)}>
                <ArrowForwardRoundedIcon sx={{ fontSize: 15 }} />
            </IconButton>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', gap: 1.5, py: 1.5 }}>
            <UserAvatar
                name={name}
                size={chatSizes.avatar}
                maxInitials={2}
                sx={[{ flex: '0 0 auto', fontSize: 14 }, chatAvatarSx(isAdmin)]}
            />
            <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 0.75,
                        mb: '3px'
                    }}>
                    <Typography
                        component='span'
                        sx={{ fontSize: 13, fontWeight: 500, lineHeight: 1.3, color: 'text.primary' }}>
                        {name}
                    </Typography>
                    {slideTags.map((tag) =>
                        tag.deleted ? (
                            <Tooltip
                                key={tag.slideItemId}
                                title='This slide was deleted'
                                placement='top'
                                arrow
                                enterTouchDelay={0}
                                leaveTouchDelay={3000}>
                                <Typography
                                    component='span'
                                    sx={chipSx((colors) => colors.deletedChip)}>
                                    {`Slide ${tag.slideNumber}`}
                                </Typography>
                            </Tooltip>
                        ) : (
                            <Typography
                                key={tag.slideItemId}
                                component='span'
                                sx={chipSx((colors) => colors.slideChip)}>
                                {`Slide ${tag.slideNumber}`}
                            </Typography>
                        )
                    )}
                    {question && (
                        <Tooltip
                            title='Question'
                            placement='top'
                            arrow
                            enterTouchDelay={0}
                            leaveTouchDelay={3000}>
                            <Typography
                                component='span'
                                aria-label='Question'
                                sx={chipSx((colors) => colors.questionChip)}>
                                ?
                            </Typography>
                        </Tooltip>
                    )}
                    <Tooltip
                        title={time.toLocaleString()}
                        placement='top-start'
                        arrow
                        enterTouchDelay={0}
                        leaveTouchDelay={3000}>
                        <Typography
                            component='span'
                            sx={{ fontSize: 12, lineHeight: 1.3, color: 'text.secondary' }}>
                            {timeAgo(time, now)}
                        </Typography>
                    </Tooltip>
                </Box>
                <Typography
                    sx={{
                        fontSize: 14,
                        lineHeight: 1.45,
                        color: 'text.primary',
                        textWrap: 'pretty',
                        overflowWrap: 'anywhere',
                        whiteSpace: 'pre-wrap'
                    }}>
                    {message}
                </Typography>
                {!readOnly && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
                        <ButtonBase onClick={() => onReply(null, '')} sx={actionSx}>
                            Reply
                        </ButtonBase>
                    </Box>
                )}
                {!readOnly && replying && !anchored && replyField}
                {replies.length > 0 && (
                    <Box>
                        <ButtonBase onClick={onToggleExpanded} sx={toggleSx}>
                            {repliesLabel}
                        </ButtonBase>
                    </Box>
                )}
                {showReplies && (
                    <Box
                        sx={(theme) => ({
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '2px',
                            mt: 0.5,
                            pl: '14px',
                            borderLeft: `2px solid ${chatColors(theme).border}`
                        })}>
                        {replies.map((reply) => (
                            <React.Fragment key={reply.id}>
                                <ChatReply
                                    name={reply.name}
                                    message={reply.message}
                                    time={reply.time}
                                    now={now}
                                    isAdmin={reply.isAdmin}
                                    onReply={readOnly ? null : () => replyToReply(reply)}
                                />
                                {anchored && reply.id === replyAnchorId && replyField}
                            </React.Fragment>
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
}
